import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type SessionInfo } from '../lib/api.js';
import { PageHeader, SectionLabel, Card, Stat, StatusPill, Row } from '../components/ui.js';

interface ContentProps {
  session: SessionInfo;
}

interface RecentPost {
  id: string;
  title: string;
  published_at: string | null;
  word_count: number | null;
  status: string;
}

interface ContentResponse {
  published: number;
  queued: number;
  refreshing: number;
  recent_posts: RecentPost[];
}

export function ContentPage({ session }: ContentProps) {
  const accountId = session.accounts[0]?.account_id ?? '';

  const { data, isLoading, isError } = useQuery<ContentResponse>({
    queryKey: ['content', accountId],
    queryFn: () => api.get<ContentResponse>(`/api/dashboard/client/content?account_id=${accountId}`),
    staleTime: 5 * 60_000,
    enabled: !!accountId,
  });

  const recentPosts: RecentPost[] = data?.recent_posts ?? [];
  const hasData = data != null;

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Content"
        title={<>Posts written by <em className="text-cream/80">Bloom</em>.</>}
        subtitle="Every post is keyword-targeted, voice-trained, schema-validated, and AI-tell scored before it goes live."
      />

      {isLoading && (
        <div className="text-sm text-cream/40">Loading content data…</div>
      )}

      {isError && (
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
          Couldn't load content data right now. Please refresh in a moment.
        </div>
      )}

      {!isLoading && !isError && !hasData && (
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
          No data yet — Bloom is collecting data for this account.
        </div>
      )}

      {hasData && (
        <>
          <section>
            <SectionLabel>Summary</SectionLabel>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Stat label="Published" value={data.published} tone="ok" />
              <Stat label="Queued" value={data.queued} hint="draft / review / scheduled" />
              <Stat label="Refreshing" value={data.refreshing} hint="update pass in progress" />
            </div>
          </section>

          {recentPosts.length > 0 && (
            <section>
              <SectionLabel>Recent posts</SectionLabel>
              <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-cream/40 border-b border-cream/[0.06]">
                      <th className="px-5 py-3 font-medium">Title</th>
                      <th className="px-3 py-3 font-medium">Status</th>
                      <th className="px-3 py-3 font-medium text-right">Words</th>
                      <th className="px-5 py-3 font-medium text-right">Published</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPosts.map((p) => (
                      <tr
                        key={p.id}
                        className="border-b border-cream/[0.04] last:border-0 hover:bg-cream/[0.02] transition-colors"
                      >
                        <td className="px-5 py-3">
                          <Link to={`/content/${p.id}`} className="text-cream/90 hover:text-cream">
                            {p.title}
                          </Link>
                        </td>
                        <td className="px-3 py-3">
                          <StatusPill
                            tone={
                              p.status === 'published' ? 'ok' :
                              p.status === 'awaiting_review' ? 'warn' :
                              p.status === 'generating' ? 'info' :
                              'neutral'
                            }
                          >
                            {p.status}
                          </StatusPill>
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums text-cream/70 text-[12px]">
                          {p.word_count != null ? p.word_count.toLocaleString() : '—'}
                        </td>
                        <td className="px-5 py-3 text-right text-cream/55 text-[12px]">
                          {p.published_at
                            ? new Date(p.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {recentPosts.length === 0 && (
            <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
              No posts yet — Bloom will start generating content as the engine warms up.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ContentDetailPage({ session }: ContentProps) {
  const { id } = useParams<{ id: string }>();
  const accountId = session.accounts[0]?.account_id ?? '';

  const { data, isLoading, isError } = useQuery<ContentResponse>({
    queryKey: ['content', accountId],
    queryFn: () => api.get<ContentResponse>(`/api/dashboard/client/content?account_id=${accountId}`),
    staleTime: 5 * 60_000,
    enabled: !!accountId,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Content" title="Loading…" />
        <div className="text-sm text-cream/40">Loading content data…</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Content" title="Error" />
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
          Couldn't load content data right now. Please refresh in a moment.
        </div>
        <Link to="/content" className="text-sm text-cream/55 hover:text-cream">← Back to all content</Link>
      </div>
    );
  }

  const post = data?.recent_posts.find((p) => p.id === id);

  if (!post) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Content" title="Post not found" />
        <Link to="/content" className="text-sm text-cream/55 hover:text-cream">← Back to all content</Link>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div>
        <Link to="/content" className="text-[11px] uppercase tracking-[0.2em] text-cream/40 hover:text-cream/85 transition-colors">
          ← All content
        </Link>
      </div>
      <PageHeader
        eyebrow="Content"
        title={post.title}
        subtitle={post.word_count != null ? `${post.word_count.toLocaleString()} words.` : undefined}
      />

      <section>
        <SectionLabel>Metadata</SectionLabel>
        <Card>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row label="Status">
              <StatusPill
                tone={
                  post.status === 'published' ? 'ok' :
                  post.status === 'awaiting_review' ? 'warn' :
                  post.status === 'generating' ? 'info' :
                  'neutral'
                }
              >
                {post.status}
              </StatusPill>
            </Row>
            <Row label="Word count">{post.word_count != null ? post.word_count.toLocaleString() : '—'}</Row>
            <Row label="Published">
              {post.published_at
                ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—'}
            </Row>
          </dl>
        </Card>
      </section>
    </div>
  );
}
